import React, { useEffect, useState } from 'react'

import PageHeader from '@instabox/internal-web-ui/components/PageHeader'
import Table from '@instabox/internal-web-ui/components/Table'
import CheckIcon from '@instabox/internal-web-ui/icons/CheckIcon'
import ErrorIcon from '@instabox/internal-web-ui/icons/ErrorIcon'
import ResetIcon from '@instabox/internal-web-ui/icons/ResetIcon'
import { Device, Package } from '@instabox/lockers-update-common/types'
import { differenceInHours } from 'date-fns'
import { orderBy } from 'lodash'

import useCurrentRollout from '../hooks/useCurrentRollout'
import useDevices from '../hooks/useDevices'

import DeviceDetailsButton from './components/DeviceDetailsButton'
import PackageDetailsButton from './components/PackageDetailsButton'
import PackageVersionDetailsButton from './components/PackageVersionDetailsButton'
import TableActionsContainer from './components/TableActionsContainer'
import TableHeaderButton from './components/TableHeaderButton'

interface SyncWarningIndicatorProps {
  synced: boolean
  deviceId: string
}

const SyncWarningIndicator: React.FC<SyncWarningIndicatorProps> = ({ synced, deviceId }) => {
  if (synced) {
    return null
  }

  return <ErrorIcon color="red" data-testid={`${deviceId}-sync-warning-indicator`} />
}

interface CompletedIndicatorProps {
  complete: boolean
  deviceId: string
}

const CompletedIndicator: React.FC<CompletedIndicatorProps> = ({ complete, deviceId }) => {
  if (complete === false) {
    return null
  }

  return <CheckIcon data-testid={`${deviceId}-completed-indicator`} />
}

const hasDeviceCompletedRollout = (target: Package[] | undefined, current: Package[]) => {
  if (target === undefined) {
    return false
  }

  return target.every((targetPackage) => {
    const match = current.find((currentPackage) => currentPackage.name === targetPackage.name)

    if (match === undefined) {
      return false
    }

    return match.version === targetPackage.version
  })
}

const hasDeviceSynced = (updatedAt?: Date) => {
  if (updatedAt === undefined) {
    return false
  }

  return differenceInHours(new Date(), new Date(updatedAt)) < 2
}

interface TargetDevice extends Device {
  complete: boolean
  synced: boolean
}

const CurrentRollout: React.FC = () => {
  const { currentRollout, loading: rolloutLoading } = useCurrentRollout()
  const { devices, loading: devicesLoading } = useDevices()
  const [targetDevices, setTargetDevices] = useState<TargetDevice[]>([])

  useEffect(() => {
    const localTargetDevices = devices
      ?.filter((d) => currentRollout?.targetDevices.includes(d.deviceId))
      .map((d) => ({
        ...d,
        complete: hasDeviceCompletedRollout(currentRollout?.packages, d.packages),
        synced: hasDeviceSynced(d.updatedAt),
      }))

    setTargetDevices(localTargetDevices)
  }, [devices, currentRollout])

  const handleHeaderClicked = (sortingKey: keyof TargetDevice) => {
    const sorted = orderBy(targetDevices, (device) => device[sortingKey])

    if (JSON.stringify(sorted) === JSON.stringify(targetDevices)) {
      setTargetDevices(sorted.reverse())
      return
    }

    setTargetDevices(sorted)
  }

  const completedDevices = targetDevices.filter((d) => d.complete)
  const completion = (Math.round((completedDevices.length / targetDevices.length) * 1000) / 10.0).toFixed(1)

  const syncedDevices = targetDevices.filter((d) => d.synced)
  const synced = (Math.round((syncedDevices.length / targetDevices.length) * 1000) / 10.0).toFixed(1)

  return (
    <div>
      <PageHeader data-testid="current-rollout-header" title={`Current Rollout`} tag="h1" />

      <div className="mb-4 d-flex align-items-center">
        <div className="me-4" title="Syncing devices" data-testid="sync-indicator">
          <ResetIcon></ResetIcon> : {syncedDevices.length} / {targetDevices.length} ({synced}%)
        </div>
        <div className="me-4" title="Completed devices" data-testid="completed-indicator">
          <CheckIcon></CheckIcon> : {completedDevices.length} / {targetDevices.length} ({completion}%)
        </div>
      </div>

      <PageHeader title="Packages" size="md" tag="h2" />
      <Table
        className="mb-4"
        isLoading={rolloutLoading}
        headers={{
          name: 'Package Name',
          version: 'Version',
          actions: '',
        }}
        rows={(currentRollout?.packages || []).map((p) => ({
          ...p,
          actions: (
            <TableActionsContainer>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, auto)', gap: 8 }}>
                <PackageDetailsButton name={p.name} label="Package Details" />
                <PackageVersionDetailsButton name={p.name} version={p.version} label="Version Details" />
              </div>
            </TableActionsContainer>
          ),
        }))}
      />

      <PageHeader title="Target Devices" size="md" tag="h2" />
      <Table
        className="mb-4"
        data-testid="target-devices-table"
        isLoading={devicesLoading}
        headers={{
          syncWarning: (
            <TableHeaderButton
              data-testid="updateWarningHeader"
              icon={<ErrorIcon />}
              onClick={() => handleHeaderClicked('updatedAt')}
            />
          ),
          deviceName: (
            <TableHeaderButton
              data-testid="deviceNameHeader"
              label="Device Name"
              onClick={() => handleHeaderClicked('deviceName')}
            />
          ),
          deviceId: (
            <TableHeaderButton
              data-testid="deviceIdHeader"
              label="Device Id"
              onClick={() => handleHeaderClicked('deviceId')}
            />
          ),
          completed: (
            <TableHeaderButton
              data-testid="completedHeader"
              label="Completed"
              onClick={() => handleHeaderClicked('complete')}
            />
          ),
          actions: '',
        }}
        rows={targetDevices.map((d) => ({
          ...d,
          syncWarning: <SyncWarningIndicator synced={d.synced} deviceId={d.deviceId} />,
          completed: <CompletedIndicator complete={d.complete} deviceId={d.deviceId} />,
          actions: (
            <TableActionsContainer>
              <DeviceDetailsButton deviceId={d.deviceId} />
            </TableActionsContainer>
          ),
        }))}
      />
    </div>
  )
}

export default CurrentRollout
